import { useEffect } from 'react';

/**
 * Hook that uses the Visual Viewport API to detect keyboard presence on mobile devices.
 * 
 * When the keyboard is visible:
 * - Sets --keyboard-offset CSS variable on document root to position fixed elements above keyboard
 * - Adds 'keyboard-visible' class to body for conditional styling
 * 
 * This is critical for Android/iOS WebView apps where the virtual keyboard
 * covers part of the viewport.
 */
export function useVisualViewport(): void {
    useEffect(() => {
        const visualViewport = window.visualViewport;

        if (!visualViewport) {
            // Visual Viewport API not supported - fallback to no offset
            document.documentElement.style.setProperty('--keyboard-offset', '0px');
            return;
        }

        // Track pending animation frame to avoid multiple updates per frame
        let rafId: number | null = null;

        const updateViewport = () => {
            // Throttle updates to once per animation frame to prevent layout thrashing
            if (rafId !== null) return;

            rafId = requestAnimationFrame(() => {
                rafId = null;

                // Calculate height difference (keyboard height)
                const keyboardHeight = window.innerHeight - visualViewport.height;
                const offsetTop = visualViewport.offsetTop || 0;

                // Footer Offset:
                // We want the footer to sit at the bottom of the VISUAL viewport.
                // Layout Viewport Bottom is at window.innerHeight.
                // Visual Viewport Bottom is at (offsetTop + visualViewport.height).
                // So we need to raise the footer by: window.innerHeight - (offsetTop + visualViewport.height)
                // Which simplifies to: (window.innerHeight - visualViewport.height) - offsetTop
                // Which is: keyboardHeight - offsetTop
                const footerOffset = keyboardHeight - offsetTop;

                document.documentElement.style.setProperty(
                    '--keyboard-offset',
                    `${footerOffset}px`
                );

                // Navbar Top:
                // We want the navbar to sit at the top of the VISUAL viewport.
                // Layout Viewport Top is at 0.
                // Visual Viewport Top is at offsetTop.
                document.documentElement.style.setProperty(
                    '--navbar-top',
                    `${offsetTop}px`
                );

                // Toggle body class for conditional styling
                if (keyboardHeight > 100) {
                    // Threshold of 100px to avoid false positives from address bar changes
                    document.body.classList.add('keyboard-visible');
                } else {
                    document.body.classList.remove('keyboard-visible');
                }
            });
        };

        // Initial update
        updateViewport();

        // Listen for viewport changes (resize, scroll within viewport)
        visualViewport.addEventListener('resize', updateViewport);
        visualViewport.addEventListener('scroll', updateViewport);

        return () => {
            // Cancel any pending animation frame
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            visualViewport.removeEventListener('resize', updateViewport);
            visualViewport.removeEventListener('scroll', updateViewport);
            document.documentElement.style.setProperty('--keyboard-offset', '0px');
            document.body.classList.remove('keyboard-visible');
        };
    }, []);
}
